import React, { useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import '../styles/PasswordRequirements.css';

/**
 * Default password requirements (used when API is unavailable)
 */
const DEFAULT_REQUIREMENTS = {
  passwordMinLength: 8,
  passwordRequireUppercase: true,
  passwordRequireLowercase: true,
  passwordRequireNumbers: true,
  passwordRequireSpecialChars: false
};

/**
 * Hook to fetch and manage password requirements
 */
export function usePasswordRequirements() {
  const [requirements, setRequirements] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRequirements();
  }, []);

  const loadRequirements = async () => {
    try {
      const response = await authAPI.getPasswordRequirements();
      setRequirements(response.data.setting_value || DEFAULT_REQUIREMENTS);
    } catch (error) {
      console.error('Failed to load password requirements:', error);
      setRequirements(DEFAULT_REQUIREMENTS);
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (password) => {
    if (!requirements || !password) return {};

    return {
      minLength: password.length >= requirements.passwordMinLength,
      uppercase: !requirements.passwordRequireUppercase || /[A-Z]/.test(password),
      lowercase: !requirements.passwordRequireLowercase || /[a-z]/.test(password),
      numbers: !requirements.passwordRequireNumbers || /\d/.test(password),
      specialChars: !requirements.passwordRequireSpecialChars || /[!@#$%^&*()_+=;':,.<>?-]/.test(password)
    };
  };

  const isPasswordValid = (password) => {
    if (!password || !requirements) return false;
    const validations = validatePassword(password);
    return Object.values(validations).every(v => v === true);
  };

  return {
    requirements,
    loading,
    validatePassword,
    isPasswordValid
  };
}

/**
 * Password Requirements Display Component
 * Shows password requirements with real-time validation feedback
 */
function PasswordRequirements({ password = '', showAlways = false, className = '' }) {
  const { requirements, loading, validatePassword } = usePasswordRequirements();

  // Don't show if loading or if password is empty and showAlways is false
  if (loading) return null;
  if (!showAlways && !password) return null;
  if (!requirements) return null;

  const validations = validatePassword(password);
  const showValidation = password.length > 0;

  return (
    <div className={`password-requirements ${className}`}>
      <p className="requirements-title">Password must have:</p>
      <ul className="requirements-list">
        <li className={showValidation ? (validations.minLength ? 'valid' : 'invalid') : ''}>
          {showValidation && (validations.minLength ? '✓' : '○')} At least {requirements.passwordMinLength} characters
        </li>
        {requirements.passwordRequireUppercase && (
          <li className={showValidation ? (validations.uppercase ? 'valid' : 'invalid') : ''}>
            {showValidation && (validations.uppercase ? '✓' : '○')} One uppercase letter (A-Z)
          </li>
        )}
        {requirements.passwordRequireLowercase && (
          <li className={showValidation ? (validations.lowercase ? 'valid' : 'invalid') : ''}>
            {showValidation && (validations.lowercase ? '✓' : '○')} One lowercase letter (a-z)
          </li>
        )}
        {requirements.passwordRequireNumbers && (
          <li className={showValidation ? (validations.numbers ? 'valid' : 'invalid') : ''}>
            {showValidation && (validations.numbers ? '✓' : '○')} One number (0-9)
          </li>
        )}
        {requirements.passwordRequireSpecialChars && (
          <li className={showValidation ? (validations.specialChars ? 'valid' : 'invalid') : ''}>
            {showValidation && (validations.specialChars ? '✓' : '○')} One special character (!@#$%^&*)
          </li>
        )}
      </ul>
    </div>
  );
}

export default PasswordRequirements;
